import { useState, useMemo, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@material-ui/core';
import { TrendingUp, TrendingDown } from '@material-ui/icons';
import Chart from 'react-apexcharts';
import { barOptions, BRAND_GREEN, BRAND_GOLD } from '../chartDefaults';
import { loansUrl } from '../../../../utils/constants';

export const currencies = [
  { refId: 1, name: 'ZWD' },
  { refId: 3, name: 'USD' }
];

const formatUSD = (v) =>
  `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const LoansDisbursedChart = ({ metric }) => {
  const [currency, setCurrency] = useState(currencies[1].refId);
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([]);

  useEffect(() => {
    getChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const selected = currencies.find((c) => c.refId === currency)?.name;
    return dataList.filter((d) => d.currency === selected);
  }, [currency, dataList]);

  const { categories, totRepayableSeries, totalPaidSeries, tableRows } = useMemo(() => {
    const grouped = filtered.reduce((acc, row) => {
      const key = row.cumulative_MY;
      if (!acc[key]) acc[key] = { totRepayable: 0, totalPaid: 0 };
      acc[key].totRepayable += row.tot_Repayable || 0;
      acc[key].totalPaid += Math.abs(row.total_Paid || 0);
      return acc;
    }, {});
    const cats = Object.keys(grouped);
    return {
      categories: cats,
      totRepayableSeries: cats.map((k) => Number(grouped[k].totRepayable.toFixed(2))),
      totalPaidSeries: cats.map((k) => Number(grouped[k].totalPaid.toFixed(2))),
      tableRows: cats.map((k) => ({
        cumulative_MY: k,
        tot_Repayable: grouped[k].totRepayable,
        total_Paid: grouped[k].totalPaid
      }))
    };
  }, [filtered]);

  const stackedOptions = {
    ...barOptions({
      categories,
      colors: [BRAND_GREEN, BRAND_GOLD],
      yFormatter: (v) => formatUSD(v)
    }),
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      fontFamily: 'inherit',
      animations: { easing: 'easeinout', speed: 600 }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        columnWidth: '45%',
        dataLabels: { position: 'center' }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (v) => formatUSD(v),
      style: { fontSize: '11px', fontWeight: 600, colors: ['#fff'] }
    },
    legend: { position: 'top', labels: { colors: '#6b6b6b' } }
  };

  const Icon = metric?.icon;
  const isUp = metric?.trend === 'up';

  const getChartData = () => {
    setLoading(true);

    const url = `${loansUrl}/api/warehouse/GetSalesReportsByCurrency?currency=3`;

    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned GetSalesReportsByCurrency Summary list', result.result);
          setDataList(result.result ?? []); // fallback to empty array
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {Icon && <Icon sx={{ color: 'primary.contrastText', fontSize: 32 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {metric?.title}
            </Typography>
            <Typography variant="h4" color="text.primary" sx={{ fontWeight: 700 }}>
              {metric?.value}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ ml: { sm: 'auto' } }}
          >
            {metric?.trendValue && (
              <Chip
                icon={isUp ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                label={metric.trendValue}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: isUp ? 'success.main' : 'error.main',
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' }
                }}
              />
            )}
            <TextField
              select
              size="small"
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              {currencies.map((c) => (
                <MenuItem key={c.refId} value={c.refId}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        <Typography variant="overline" color="text.secondary">
          Total repayable vs total paid by cumulative month
        </Typography>
        <Box sx={{ width: '100%', height: { xs: 280, md: 380 }, mt: 1 }}>
          <Chart
            options={stackedOptions}
            series={[
              { name: 'Total Repayable', data: totRepayableSeries },
              { name: 'Total Paid', data: totalPaidSeries }
            ]}
            type="bar"
            height="100%"
            width="100%"
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1, fontWeight: 700 }}>
            Monthly breakdown
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Cumulative MY</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Tot Repayable
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Total Paid
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row) => (
                  <TableRow key={row.cumulative_MY} hover>
                    <TableCell>{row.cumulative_MY}</TableCell>
                    <TableCell align="right">{formatUSD(row.tot_Repayable)}</TableCell>
                    <TableCell align="right">{formatUSD(row.total_Paid)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LoansDisbursedChart;
